import { useEffect, useState } from "react";
import { Alert, Button, Checkbox, Group, Modal, Paper, ScrollArea, Select, Stack, Table, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { SheetExportResult, SheetExportSkipped } from "../../api/types";
import { ResponsiveTable } from "../responsiveTable/ResponsiveTable";

export function ExportToSheetModal({
  opened,
  onClose,
  target = "shipments",
  ids = [],
  boxId,
  filter = "all",
}: {
  opened: boolean;
  onClose: () => void;
  target?: "shipments" | "defects" | "box" | "all_returns";
  ids?: number[];
  boxId?: number;
  filter?: "all" | "repaired" | "defect";
}) {
  const { t } = useTranslation();
  const [tabs, setTabs] = useState<string[]>([]);
  const [docName, setDocName] = useState("");
  const [tab, setTab] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);
  const [skipped, setSkipped] = useState<SheetExportSkipped[]>([]);
  // After a shipments export: ask what to send to the Telegram groups.
  const [groups, setGroups] = useState<{
    activation: { text: string; link: string | null } | null;
    attachments: { photos: number; documents: number } | null;
  } | null>(null);
  const [sendAct, setSendAct] = useState(true);
  const [sendAtt, setSendAtt] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!opened) return;
    setSkipped([]); setTab(null); setNotConfigured(false); setGroups(null);
    setLoading(true);
    // "box"/"all_returns" exports go to the defects spreadsheet, so list its tabs.
    api.sheetTabs(target === "shipments" ? "shipments" : "defects")
      .then((r) => { setTabs(r.tabs); setDocName(r.spreadsheet_name); })
      .catch((e) => {
        if (e instanceof ApiError && (e.detail as { error?: string } | null)?.error === "not_configured") {
          setNotConfigured(true);
        } else {
          notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
        }
      })
      .finally(() => setLoading(false));
  }, [opened]);

  function errMsg(e: unknown): string {
    const code = e instanceof ApiError ? (e.detail as { error?: string } | null)?.error : undefined;
    if (code === "no_dev_eui_column") return t("shipments.exportSheetNoDevEui");
    if (code === "tab_not_found") return t("shipments.exportSheetTabNotFound");
    if (code === "not_configured") return t("shipments.exportSheetNotConfigured");
    if (code === "drive_not_authenticated" || code === "drive_not_configured") return t("shipments.exportSheetDriveAuth");
    if (code === "sheets_error") return t("shipments.exportSheetSheetsError");
    return e instanceof ApiError ? e.message : String(e);
  }

  async function run(force: boolean, devEuis?: string[]) {
    if (!tab) return;
    setBusy(true);
    try {
      const res: SheetExportResult =
        target === "box"
          ? await api.exportBoxToSheet({ box_id: boxId!, filter, tab, force, dev_euis: devEuis })
          : target === "all_returns"
            ? await api.exportAllReturnsToSheet({ tab, force, dev_euis: devEuis })
            : target === "defects"
              ? await api.exportDevicesToSheet({ device_ids: ids, tab, force, dev_euis: devEuis })
              : await api.exportToSheet({ shipment_ids: ids, tab, force, dev_euis: devEuis });
      notifications.show({
        color: "green",
        message: t("shipments.exportSheetDone2", { added: res.appended, updated: res.updated }),
      });
      setSkipped(force ? [] : res.skipped);
      if (force || res.skipped.length === 0) {
        if (target === "shipments" && await offerGroups()) return;
        onClose();
      }
    } catch (e) {
      notifications.show({ color: "red", message: errMsg(e) });
    } finally {
      setBusy(false);
    }
  }

  // Returns true if the modal should stay open to ask about the Telegram groups.
  async function offerGroups(): Promise<boolean> {
    if (!tab) return false;
    try {
      const r = await api.exportToSheetGroups({ shipment_ids: ids, tab });
      const activation = r.activation ?? null;
      const attachments = r.attachments ?? null;
      if (!activation && !attachments) return false;
      setSendAct(true); setSendAtt(true);
      setGroups({ activation, attachments });
      return true;
    } catch {
      return false;
    }
  }

  async function sendGroups() {
    if (!tab || !groups) return;
    const send_activation = !!groups.activation && sendAct;
    const send_attachments = !!groups.attachments && sendAtt;
    if (!send_activation && !send_attachments) { onClose(); return; }
    setSending(true);
    try {
      await api.exportToSheetGroups({ shipment_ids: ids, tab, send_activation, send_attachments });
      notifications.show({ color: "green", message: t("shipments.exportGroupsSent") });
      onClose();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setSending(false);
    }
  }

  if (groups) {
    const nothing = !(groups.activation && sendAct) && !(groups.attachments && sendAtt);
    return (
      <Modal opened={opened} onClose={onClose} title={t("shipments.exportGroupsTitle")} size="lg">
        <Stack>
          <Text size="sm">{t("shipments.exportGroupsAsk")}</Text>
          {groups.activation && (
            <Stack gap="xs">
              <Checkbox
                label={t("shipments.exportGroupsActivation")}
                checked={sendAct}
                onChange={(e) => setSendAct(e.currentTarget.checked)}
              />
              <Paper withBorder p="sm" bg="var(--mantine-color-default-hover)" opacity={sendAct ? 1 : 0.5}>
                <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>{groups.activation.text}</Text>
                {groups.activation.link && (
                  <Text size="xs" c="dimmed" mt="xs" style={{ wordBreak: "break-all" }}>{groups.activation.link}</Text>
                )}
              </Paper>
            </Stack>
          )}
          {groups.attachments && (
            <Checkbox
              label={t("shipments.exportGroupsAttachments", {
                photos: groups.attachments.photos,
                documents: groups.attachments.documents,
              })}
              checked={sendAtt}
              onChange={(e) => setSendAtt(e.currentTarget.checked)}
            />
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose} disabled={sending}>
              {t("shipments.exportGroupsNo")}
            </Button>
            <Button onClick={() => void sendGroups()} loading={sending} disabled={nothing}>
              {t("shipments.exportGroupsYes")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("shipments.exportSheetTitle")} size="lg">
      {notConfigured ? (
        <Alert color="yellow">{t("shipments.exportSheetNotConfigured")}</Alert>
      ) : (
        <Stack>
          <Text size="sm" c="dimmed">{docName}</Text>
          <Select
            label={t("shipments.exportSheetTab")}
            data={tabs}
            value={tab}
            onChange={setTab}
            searchable
            disabled={loading}
          />
          <Group justify="flex-end">
            <Button onClick={() => void run(false)} loading={busy} disabled={!tab}>
              {t("shipments.exportSheetGo")}
            </Button>
          </Group>
          {skipped.length > 0 && (
            <>
              <Text fw={600} size="sm">{t("shipments.exportSheetSkippedTitle", { count: skipped.length })}</Text>
              <ScrollArea h={200}>
              <ResponsiveTable
                mobile={{
                  rows: skipped,
                  rowKey: (s) => s.dev_eui,
                  title: (s) => s.dev_eui,
                  subtitle: (s) => s.model,
                  fields: [{ label: "Cod", render: (s) => s.shipment_code }],
                }}
              >
                <Table striped>
                  <Table.Thead>
                    <Table.Tr><Table.Th>DEV_EUI</Table.Th><Table.Th>Model</Table.Th><Table.Th>Cod</Table.Th></Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {skipped.map((s) => (
                      <Table.Tr key={s.dev_eui}>
                        <Table.Td ff="monospace">{s.dev_eui}</Table.Td>
                        <Table.Td>{s.model}</Table.Td>
                        <Table.Td>{s.shipment_code}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ResponsiveTable>
              </ScrollArea>
              <Group justify="flex-end">
                <Button color="orange" loading={busy} onClick={() => void run(true, skipped.map((s) => s.dev_eui))}>
                  {t("shipments.exportSheetForce", { count: skipped.length })}
                </Button>
              </Group>
            </>
          )}
        </Stack>
      )}
    </Modal>
  );
}
